"use client";
import { useEffect, useRef } from "react";

const SRC = "https://analytics.shimesu.eu/script.js";
const WEBSITE_ID = "d6cd31c3-3bc7-438f-9e0d-b6f0e5bd94d1";

function performanceAccepted(): boolean {
  const hit = document.cookie.split("; ").find((c) => c.startsWith("cookie_consent="));
  if (!hit) return false;
  try {
    const parsed = JSON.parse(decodeURIComponent(hit.split("=").slice(1).join("=")));
    return parsed.performance === true;
  } catch {
    return false;
  }
}

function inject() {
  if (document.getElementById("umami-script")) return; // already loaded
  const s = document.createElement("script");
  s.id = "umami-script";
  s.async = true;
  s.defer = true;
  s.src = SRC;
  s.setAttribute("data-website-id", WEBSITE_ID);
  s.setAttribute("data-domains", "shimesu.eu");
  document.head.appendChild(s);
}

/**
 * Loads the Umami tracking script only when the visitor has accepted the
 * "Performance" cookie category. Server-side, the layout already renders this
 * component only when performance was accepted at request time; this client
 * component additionally injects the script if the visitor accepts LATER in the
 * same page view (via the shimesu-consent event), so tracking starts without a
 * reload. Umami is cookieless, so if consent is later revoked the script simply
 * won't load on the next visit — nothing to clean up.
 */
export function Analytics({ initiallyAccepted }: { initiallyAccepted: boolean }) {
  const done = useRef(false);
  useEffect(() => {
    if (initiallyAccepted && performanceAccepted()) {
      inject();
      done.current = true;
    }
    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.performance === true && !done.current) {
        inject();
        done.current = true;
      }
    };
    window.addEventListener("shimesu-consent", onConsent);
    return () => window.removeEventListener("shimesu-consent", onConsent);
  }, [initiallyAccepted]);
  return null;
}
